import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { API_URL } from "../data/content";
import { OVERVIEW_IMG, FITS, WOMEN_MEASUREMENTS, MEN_MEASUREMENTS, KIDS_MEASUREMENTS } from "../data/measurements";
import { Tag, EASE, SolidButton } from "../components/Shared";
import Tailor from "../components/Tailor";

const api = axios.create({ baseURL: API_URL, withCredentials: true });

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const inputCls =
  "w-full border border-line/15 bg-bone/5 px-4 py-3.5 text-sm text-bone placeholder:text-fog/60 focus:border-flame focus:outline-none transition-colors duration-300";

const AuthCard = ({ onAuth }) => {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/auth/${mode}`, form);
      onAuth(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-md border border-line/10 bg-coal p-8 sm:p-10" data-testid="measure-auth-card">
      <div className="mb-8 grid grid-cols-2 gap-px bg-line/10">
        {["register", "login"].map((m2) => (
          <button
            key={m2}
            type="button"
            onClick={() => { setMode(m2); setError(""); }}
            data-testid={`measure-auth-${m2}-tab`}
            className={`bg-ink px-4 py-3.5 font-mono text-[11px] uppercase tracking-[0.3em] transition-colors duration-300 ${
              mode === m2 ? "text-flame" : "text-fog hover:text-bone"
            }`}
          >
            {m2 === "register" ? "Create Account" : "Sign In"}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="measure-auth-form">
        {mode === "register" && (
          <>
            <input required data-testid="measure-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
            <input data-testid="measure-phone-input" className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} />
          </>
        )}
        <input required type="email" data-testid="measure-email-input" className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} />
        <input required type="password" data-testid="measure-password-input" className={inputCls} placeholder="Password (min 6 characters)" value={form.password} onChange={set("password")} />
        {error && <p className="font-mono text-xs text-flame" data-testid="measure-auth-error">{error}</p>}
        <SolidButton type="submit" testId="measure-auth-submit-button">
          {busy ? "Please wait..." : mode === "register" ? "Begin — Create Account" : "Sign In"}
        </SolidButton>
      </form>
      <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-wide text-fog">
        Your measurements are private to your account and shared only with the atelier.
      </p>
    </div>
  );
};

const StepCard = ({ selected, onClick, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`border p-6 text-left transition-all duration-300 ${
      selected ? "border-flame bg-flame/5" : "border-line/15 hover:border-flame/50"
    }`}
  >
    {children}
  </button>
);

const Onboarding = ({ existing, onDone }) => {
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(existing || { gender: "", units: "", height: "", fit: "Regular" });
  const [busy, setBusy] = useState(false);

  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/measure/profile", profile);
      onDone(profile);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="measure-onboarding">
      <div className="mb-10 flex items-center gap-2">
        {[0, 1, 2].map((i) => (
          <span key={i} className={`h-0.5 flex-1 transition-colors duration-500 ${step >= i ? "bg-flame" : "bg-line/15"}`} />
        ))}
      </div>
      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="s0" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.45, ease: EASE }}>
            <h2 className="font-serif text-4xl font-light sm:text-5xl">Who are we measuring?</h2>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[["women", "Women", WOMEN_MEASUREMENTS.length], ["men", "Men", MEN_MEASUREMENTS.length], ["kids", "Kids", KIDS_MEASUREMENTS.length]].map(([g, label, count]) => (
                <StepCard key={g} selected={profile.gender === g} onClick={() => setProfile((p) => ({ ...p, gender: g }))} testId={`onboarding-gender-${g}`}>
                  <p className="font-serif text-2xl font-normal">{label}</p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">{count} measurements</p>
                </StepCard>
              ))}
            </div>
            <div className="mt-10">
              <SolidButton testId="onboarding-next-0" onClick={() => profile.gender && setStep(1)}>Continue</SolidButton>
            </div>
          </motion.div>
        )}
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.45, ease: EASE }}>
            <h2 className="font-serif text-4xl font-light sm:text-5xl">Your preferred units.</h2>
            <div className="mt-10 grid grid-cols-2 gap-4">
              {[["in", "Inches", "in — the atelier standard"], ["cm", "Centimetres", "cm — metric"]].map(([u, label, note]) => (
                <StepCard key={u} selected={profile.units === u} onClick={() => setProfile((p) => ({ ...p, units: u }))} testId={`onboarding-units-${u}`}>
                  <p className="font-serif text-2xl font-normal">{label}</p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">{note}</p>
                </StepCard>
              ))}
            </div>
            <div className="mt-10 flex gap-4">
              <button type="button" onClick={() => setStep(0)} data-testid="onboarding-back-1" className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog transition-colors hover:text-flame">← Back</button>
              <SolidButton testId="onboarding-next-1" onClick={() => profile.units && setStep(2)}>Continue</SolidButton>
            </div>
          </motion.div>
        )}
        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.45, ease: EASE }}>
            <h2 className="font-serif text-4xl font-light sm:text-5xl">Height & preferred fit.</h2>
            <div className="mt-10">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-fog">Your Height ({profile.units === "in" ? "inches" : "cm"})</p>
              <input
                inputMode="decimal"
                data-testid="onboarding-height-input"
                className={`${inputCls} max-w-xs font-serif text-3xl`}
                placeholder={profile.units === "in" ? "65" : "165"}
                value={profile.height}
                onChange={(e) => setProfile((p) => ({ ...p, height: e.target.value.replace(/[^0-9.]/g, "") }))}
              />
            </div>
            <div className="mt-8">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-fog">Preferred Fit</p>
              <div className="grid grid-cols-3 gap-3">
                {FITS.map((f) => (
                  <StepCard key={f} selected={profile.fit === f} onClick={() => setProfile((p) => ({ ...p, fit: f }))} testId={`onboarding-fit-${f.toLowerCase()}`}>
                    <p className="font-serif text-xl font-normal">{f}</p>
                  </StepCard>
                ))}
              </div>
            </div>
            <div className="mt-10 flex gap-4">
              <button type="button" onClick={() => setStep(1)} data-testid="onboarding-back-2" className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog transition-colors hover:text-flame">← Back</button>
              <SolidButton testId="onboarding-finish-button" onClick={finish}>{busy ? "Saving..." : "Enter the Atelier"}</SolidButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function MeasurePage() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return (
    <div className="min-h-screen bg-ink px-5 pb-32 pt-32 sm:px-10 md:pt-40" data-testid="measure-page">
      <div className="mx-auto max-w-[1400px]">
        {user === null && (
          <div className="flex min-h-[40vh] items-center justify-center">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-fog">Preparing the atelier…</span>
          </div>
        )}

        {user === false && (
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <div>
              <Tag>The Digital Tailor</Tag>
              <h1 className="mt-6 font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-7xl">
                MEASURED ONCE.<br /><span className="text-flame">MADE FOR YOU.</span>
              </h1>
              <p className="mt-6 max-w-md text-lg font-light leading-relaxed text-fog">
                Register, follow the illustrated guide, and record your body measurements — the atelier
                keeps them on file for every bespoke commission, so every garment begins with you.
              </p>
              <div className="mt-10 hidden lg:block" data-testid="measure-figure-trio">
                <img src={OVERVIEW_IMG.replace(".webp", "-d.webp")} alt="Measurement guide — front, side and back" className="logo-for-dark w-full max-w-md opacity-90" />
                <img src={OVERVIEW_IMG.replace(".webp", "-l.webp")} alt="" aria-hidden="true" className="logo-for-light w-full max-w-md opacity-90" />
              </div>
            </div>
            <div className="flex justify-start lg:justify-center">
              <AuthCard onAuth={setUser} />
            </div>
          </div>
        )}

        {user && !user.profile && <Onboarding onDone={(profile) => setUser((u) => ({ ...u, profile }))} />}

        {user && user.profile && (
          <>
            {user.role === "admin" && (
              <Link to="/atelier" data-testid="atelier-dashboard-link" className="mb-10 flex items-center justify-between border border-flame/40 bg-flame/10 px-6 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-flame transition-colors duration-300 hover:bg-flame hover:text-ink">
                Atelier Dashboard — view all client measurement files <span>→</span>
              </Link>
            )}
            <Tailor
              user={user}
              onLogout={logout}
              onEditProfile={() => setUser((u) => ({ ...u, profile: null }))}
            />
          </>
        )}
      </div>
    </div>
  );
}
